import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { UserPlus, Trash2, Users, Pencil, X, Check } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import type { PropertyShare, ShareRole } from '@/types';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmModal } from '@/components/ui/confirm-modal';

const shareSchema = z.object({
  email: z.string().email('Ungültige E-Mail-Adresse'),
  role: z.enum(['viewer', 'co_owner']),
  equity_share: z.string().optional(),
});

type ShareFormData = z.infer<typeof shareSchema>;

interface PropertySharingProps {
  propertyId: string;
  shares: PropertyShare[];
  ownerId: string;
  onSharesUpdate: () => void;
}

export function PropertySharing({ propertyId, shares, ownerId, onSharesUpdate }: PropertySharingProps) {
  const { user } = useAuthStore();
  const [isAdding, setIsAdding] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingShare, setDeletingShare] = useState<PropertyShare | null>(null);
  const [editingShare, setEditingShare] = useState<PropertyShare | null>(null);
  const [editRole, setEditRole] = useState<ShareRole>('viewer');
  const [editEquity, setEditEquity] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);

  const isOwner = user?.id === ownerId;

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<ShareFormData>({
    resolver: zodResolver(shareSchema),
    defaultValues: {
      role: 'viewer',
    },
  });

  const onSubmit = async (data: ShareFormData) => {
    try {
      // Find user by email using secure RPC function (bypasses RLS)
      const { data: users, error: lookupError } = await supabase
        .rpc('lookup_user_by_email', { lookup_email: data.email.toLowerCase() });

      if (lookupError) throw lookupError;
      if (!users || users.length === 0) {
        toast.error('Benutzer nicht gefunden', {
          description: 'Es gibt keinen Benutzer mit dieser E-Mail-Adresse.'
        });
        return;
      }

      const targetUser = users[0];

      // Check if already shared
      if (shares.some(s => s.user_id === targetUser.id)) {
        toast.error('Bereits geteilt', {
          description: 'Diese Immobilie wurde bereits mit diesem Benutzer geteilt.'
        });
        return;
      }

      // Check if trying to share with self
      if (targetUser.id === user?.id) {
        toast.error('Ungültig', {
          description: 'Du kannst die Immobilie nicht mit dir selbst teilen.'
        });
        return;
      }

      // Create share
      const { error: shareError } = await supabase
        .from('property_shares')
        .insert({
          property_id: propertyId,
          user_id: targetUser.id,
          role: data.role,
          equity_share: data.equity_share ? parseFloat(data.equity_share) / 100 : null,
          invited_by: user?.id,
        });

      if (shareError) throw shareError;

      toast.success('Erfolgreich geteilt', {
        description: `Die Immobilie wurde mit ${data.email} geteilt.`
      });
      reset();
      setIsAdding(false);
      onSharesUpdate();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Teilen';
      console.error('Share error:', error);
      toast.error('Fehler beim Teilen', { description: message });
    }
  };

  const handleDeleteShare = async () => {
    if (!deletingShare) return;

    try {
      const { error } = await supabase
        .from('property_shares')
        .delete()
        .eq('id', deletingShare.id);

      if (error) throw error;

      toast.success('Zugriff entfernt');
      onSharesUpdate();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Entfernen';
      console.error('Delete share error:', error);
      toast.error('Fehler beim Entfernen', { description: message });
    } finally {
      setDeleteModalOpen(false);
      setDeletingShare(null);
    }
  };

  const startEditing = (share: PropertyShare) => {
    setEditingShare(share);
    setEditRole(share.role);
    setEditEquity(share.equity_share ? (share.equity_share * 100).toString() : '');
  };

  const cancelEditing = () => {
    setEditingShare(null);
    setEditRole('viewer');
    setEditEquity('');
  };

  const handleUpdateShare = async () => {
    if (!editingShare) return;

    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from('property_shares')
        .update({
          role: editRole,
          equity_share: editEquity ? parseFloat(editEquity) / 100 : null,
        })
        .eq('id', editingShare.id);

      if (error) throw error;

      toast.success('Zugriff aktualisiert');
      cancelEditing();
      onSharesUpdate();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Aktualisieren';
      console.error('Update share error:', error);
      toast.error('Fehler beim Aktualisieren', { description: message });
    } finally {
      setIsUpdating(false);
    }
  };

  const getRoleLabel = (role: ShareRole) => {
    return role === 'co_owner' ? 'Miteigentümer' : 'Betrachter';
  };

  if (!isOwner) {
    return null;
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Zugriff teilen</h2>
            <p className="text-sm text-muted-foreground">
              Immobilie mit anderen Nutzern teilen
            </p>
          </div>
        </div>
        {!isAdding && (
          <Button onClick={() => setIsAdding(true)} size="sm">
            <UserPlus className="w-4 h-4 mr-2" />
            Hinzufügen
          </Button>
        )}
      </div>

      {isAdding && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mb-6 p-4 border rounded-lg">
          <div className="space-y-2">
            <Label htmlFor="email">E-Mail-Adresse</Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              {...register('email')}
              error={errors.email?.message}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">Rolle</Label>
            <select
              id="role"
              {...register('role')}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="viewer">Betrachter (nur lesen)</option>
              <option value="co_owner">Miteigentümer (bearbeiten)</option>
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="equity_share">Eigenkapitalanteil % (optional)</Label>
            <Input
              id="equity_share"
              type="number"
              min="0"
              max="100"
              step="0.01"
              placeholder="z.B. 50"
              {...register('equity_share')}
              error={errors.equity_share?.message}
            />
          </div>

          <div className="flex gap-2">
            <Button type="submit" isLoading={isSubmitting}>
              Teilen
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsAdding(false);
                reset();
              }}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      )}

      {shares.length > 0 ? (
        <div className="space-y-2">
          {shares.map((share) => (
            <div
              key={share.id}
              className="p-3 rounded-lg border hover:bg-muted/50 transition-colors"
            >
              {editingShare?.id === share.id ? (
                // Bearbeitungsmodus
                <div className="space-y-3">
                  <p className="font-medium">{share.user?.email || 'Unbekannt'}</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Rolle</Label>
                      <select
                        value={editRole}
                        onChange={(e) => setEditRole(e.target.value as ShareRole)}
                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                      >
                        <option value="viewer">Betrachter (nur lesen)</option>
                        <option value="co_owner">Miteigentümer (bearbeiten)</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Anteil %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="z.B. 50"
                        value={editEquity}
                        onChange={(e) => setEditEquity(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleUpdateShare} disabled={isUpdating}>
                      <Check className="w-4 h-4 mr-1" />
                      Speichern
                    </Button>
                    <Button size="sm" variant="ghost" onClick={cancelEditing}>
                      <X className="w-4 h-4 mr-1" />
                      Abbrechen
                    </Button>
                  </div>
                </div>
              ) : (
                // Anzeigemodus
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-medium">
                      {share.user?.email || 'Unbekannt'}
                    </p>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                      <span>{getRoleLabel(share.role)}</span>
                      {share.equity_share != null && (
                        <span>• {(share.equity_share * 100).toFixed(1)}% Anteil</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEditing(share)}
                      title="Bearbeiten"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setDeletingShare(share);
                        setDeleteModalOpen(true);
                      }}
                      title="Entfernen"
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        !isAdding && (
          <p className="text-sm text-muted-foreground text-center py-8">
            Diese Immobilie wurde noch nicht geteilt.
          </p>
        )
      )}

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeletingShare(null);
        }}
        onConfirm={handleDeleteShare}
        title="Zugriff entfernen"
        description={`Möchtest du den Zugriff für ${deletingShare?.user?.email || 'diesen Benutzer'} wirklich entfernen?`}
      />
    </Card>
  );
}
